import {AxiosError} from "axios";
import {AtSign as MTAIcon, Settings as EditMainConfigIcon} from "iconoir-react";
import {useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from "react-bootstrap/Form";
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {MTAApi, StandardMainSettings} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {DialogCloseCause} from "../common/Enums";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {MTAConfigPopup} from "./MTAConfigPopup";

const mtaApi = new MTAApi(ApiConfig.config());

export const MTASettings = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * True if settings are loaded
     */
    const [loaded, setLoaded] = useState<boolean>(false);
    /**
     * Postfix my hostname
     */
    const [myHostname, setMyHostname] = useState<string>("");
    /**
     * Postfix my networks
     */
    const [myNetworks, setMyNetworks] = useState<string[]>([]);
    /**
     * Network to add
     */
    const [newNetwork, setNewNetwork] = useState<string>("");
    /**
     * Selected Network
     */
    const [selectedNetwork, setSelectedNetwork] = useState<string>("");
    /**
     * Postfix external relay hosts
     */
    const [externalRelayHosts, setExternalRelayHosts] = useState<string>("");
    /**
     * Postfix internal relay hosts
     */
    const [internalRelayHosts, setInternalRelayHosts] = useState<string>("");
    /**
     * Postfix Reject Unverified Recipient
     */
    const [rejectUnverifiedRecipient, setRejectUnverifiedRecipient] = useState<boolean>(false);
    /**
     * Postfix Unverified Recipient Reject Code
     */
    const [unverifiedRecipientRejectCode, setUnverifiedRecipientRejectCode] = useState<number>(450);
    /**
     * Postfix relay domains
     */
    const [relayDomains, setRelayDomains] = useState<string[]>([]);
    /**
     * Relay domain to add
     */
    const [newRelayDomain, setNewRelayDomain] = useState<string>("");
    /**
     * Selected Relay Domain
     */
    const [selectedRelayDomain, setSelectedRelayDomain] = useState<string>("");
    /**
     * Postfix Message size limit
     */
    const [messageSizeLimit, setMessageSizeLimit] = useState<number>(0);
    /**
     * Postfix help name
     */
    const [heloName, setHeloName] = useState<string>("");
    /**
     * If true, the "MTA config" modal dialog will be shown
     */
    const [showMTAConfigPopup, setShowMTAConfigPopup] = useState<boolean>(false);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while saving to show a spinner on the download button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * If true, settings will be reloaded
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load main settings")

        // reset state
        setForceReload(false);
        setLoaded(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetStandardMainSettings()
            .then(response => {
                const settings :  StandardMainSettings = response.data;

                setMyHostname(settings.myHostname??"");
                setMyNetworks(settings.myNetworks??[]);
                setExternalRelayHosts(settings.externalRelayHosts??"");
                setInternalRelayHosts(settings.internalRelayHosts??"");
                setRejectUnverifiedRecipient(settings.rejectUnverifiedRecipient??false);
                setUnverifiedRecipientRejectCode(settings.unverifiedRecipientRejectCode??450);
                setRelayDomains(settings.relayDomains??[]);
                setMessageSizeLimit(settings.messageSizeLimit??10000000)
                setHeloName(settings.smtpHeloName??"")
                setLoaded(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting main settings", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        forceReload,
        t]);

    const addNetwork = () =>
    {
        if (!myNetworks.includes(newNetwork))
        {
            setMyNetworks([...myNetworks, newNetwork]);
            setNewNetwork("");
        }
    }

    const removeNetwork = () =>
    {
        setMyNetworks([...myNetworks].filter(nw => nw !== selectedNetwork));
        setSelectedNetwork("")
    }

    const addRelayDomain = () =>
    {
        if (!relayDomains.includes(newRelayDomain)) {
            setRelayDomains([...relayDomains, newRelayDomain]);
            setNewRelayDomain("");
        }
    }

    const removeRelayDomain = () =>
    {
        setRelayDomains([...relayDomains].filter(rd => rd !== selectedRelayDomain));
        setSelectedRelayDomain("")
    }

    /**
     * Is called when the MTA Config Popup is closed
     */
    const onMTAConfigPopupClose = (closeCause : DialogCloseCause) =>
    {
        setShowMTAConfigPopup(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    const save = () =>
    {
        logger.debug("Save main settings")

        // reset error message
        setErrorMessage(undefined);
        setSaving(true);

        const settings :  StandardMainSettings = {
            myHostname : myHostname,
            myNetworks : myNetworks,
            externalRelayHosts : externalRelayHosts,
            internalRelayHosts : internalRelayHosts,
            rejectUnverifiedRecipient : rejectUnverifiedRecipient,
            unverifiedRecipientRejectCode : unverifiedRecipientRejectCode,
            relayDomains : relayDomains,
            messageSizeLimit : messageSizeLimit,
            smtpHeloName : heloName
        };

        mtaApi.mTAControllerSetStandardMainSettings(settings)
            .then(() => {
                // restart postfix
                mtaApi.mTAControllerRestartPostfix().then(() => {
                    setForceReload(true);
                })
                .catch((err: Error | AxiosError) => {
                    logger.error("Error restarting postfix", err);
                    Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                    setErrorMessage(Utils.getErrorMessage(err, t));
                })
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting main settings", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <>
            {showMTAConfigPopup && <MTAConfigPopup onClose={onMTAConfigPopupClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("mta-settings.title")} icon={<MTAIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowMTAConfigPopup(true)}>
                                <EditMainConfigIcon className="text-success"/> {t('mta-settings.edit-main-config')}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <div className="row flex-grow-1 overflow-auto vh-100">
                <Form>
                    <Col lg={6} md={12}>
                        <FloatingLabel label={t('mta-settings.my-host-name')} className="text-secondary mb-3">
                            <Form.Control type="text" value={myHostname} placeholder={t('mta-settings.my-host-name')}
                                          onChange={e => setMyHostname(e.target.value)}/>
                        </FloatingLabel>

                        <div className="mb-3">
                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t('mta-settings.relay-domains')}</Form.Label>
                                <Form.Select htmlSize={5} onChange={(e) => setSelectedRelayDomain(e.target.value)}>
                                    {relayDomains.map((nw) => (<option key={nw} value={nw}>{nw}</option>))}
                                </Form.Select>
                            </Form.Group>
                            <InputGroup>
                                <Form.Control value={newRelayDomain} onChange={e => setNewRelayDomain(e.target.value)}/>
                                <Button size="sm"
                                        variant="primary"
                                        onClick={() => addRelayDomain()}
                                        disabled={StringUtils.isBlank(newRelayDomain) || relayDomains.includes(newRelayDomain)}>
                                    {t('mta-settings.add-relay-domain')}
                                </Button>
                                <Button size="sm"
                                        variant="warning"
                                        onClick={() => removeRelayDomain()}
                                        disabled={StringUtils.isBlank(selectedRelayDomain)}>
                                    {t('mta-settings.remove-selected')}
                                </Button>
                            </InputGroup>
                        </div>

                        <FloatingLabel label={t('mta-settings.external-relay-host')} className="text-secondary mb-3">
                            <Form.Control type="text" value={externalRelayHosts} placeholder={t('mta-settings.external-relay-host')}
                                          onChange={e => setExternalRelayHosts(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t('mta-settings.internal-relay-host')} className="text-secondary mb-3">
                            <Form.Control type="text" value={internalRelayHosts} placeholder={t('mta-settings.internal-relay-host')}
                                          onChange={e => setInternalRelayHosts(e.target.value)}/>
                        </FloatingLabel>

                        <Form.Group className="mb-3">
                            <Form.Check className="text-secondary" type="switch" label={t('mta-settings.reject-unverified-recipient')}
                                        checked={rejectUnverifiedRecipient}
                                        onChange={e => setRejectUnverifiedRecipient(e.target.checked)}/>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="text-secondary">{t('mta-settings.unverified-recipient-reject-code')}</Form.Label>
                            <Form.Select value={unverifiedRecipientRejectCode}
                                         onChange={e => setUnverifiedRecipientRejectCode(Number(e.target.value))}>
                                <option value="450">450</option>
                                <option value="550">550</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="mb-3">
                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t('mta-settings.my-networks')}</Form.Label>
                                <Form.Select htmlSize={5} onChange={(e) => setSelectedNetwork(e.target.value)}>
                                    {myNetworks.map((nw) => (<option key={nw} value={nw}>{nw}</option>))}
                                </Form.Select>
                            </Form.Group>
                            <InputGroup>
                                <Form.Control value={newNetwork} onChange={e => setNewNetwork(e.target.value)}/>
                                <Button size="sm"
                                        variant="primary"
                                        onClick={() => addNetwork()}
                                        disabled={StringUtils.isBlank(newNetwork) || myNetworks.includes(newNetwork)}>
                                    {t('mta-settings.add-network')}
                                </Button>
                                <Button size="sm"
                                        variant="warning"
                                        onClick={() => removeNetwork()}
                                        disabled={StringUtils.isBlank(selectedNetwork)}>
                                    {t('mta-settings.remove-selected')}
                                </Button>
                            </InputGroup>
                        </div>

                        <FloatingLabel label={t('mta-settings.message-size-limit')} className="text-secondary mb-3">
                            <Form.Control type="number" min={0} max={1073741824} value={messageSizeLimit} placeholder={t('mta-settings.message-size-limit')}
                                          onChange={e => setMessageSizeLimit(Number(e.target.value))}/>
                        </FloatingLabel>

                        <FloatingLabel label={t('mta-settings.helo-name')} className="text-secondary mb-3">
                            <Form.Control type="text" value={heloName} placeholder={t('mta-settings.helo-name')}
                                          onChange={e => setHeloName(e.target.value)}/>
                        </FloatingLabel>
                    </Col>
                </Form>
            </div>
            <Row>
                <div className="col pt-3 pb-3 border-top border-bottom">
                    {/* show an alert if a validation error occurred */}
                    <RenderAlert message={errorMessage}/>

                    <Button
                        variant="success"
                        type="submit"
                        onClick={() => save()}
                        disabled={!loaded || spinnerStateCounter > 0}
                    >
                        {saving && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {saving ? t("common.saving") : t("common.save")}
                    </Button>
                </div>
            </Row>
        </>
    )
}
